import { type ReactNode, useId } from "react";
import { ChevronDownIcon } from "./review-icons";

/**
 * The pieces the review screen and the card page are built from, so a section,
 * a field and a hint read the same on both, as iOS's do.
 */

export function SectionTitle({ children, warn = false }: { children: ReactNode; warn?: boolean }) {
  return (
    <h2 className={`text-[11px] font-semibold tracking-wide uppercase ${warn ? "text-warn" : "text-faint"}`}>
      {children}
    </h2>
  );
}

export function Hint({ children, warn = false }: { children: ReactNode; warn?: boolean }) {
  return <p className={`text-xs ${warn ? "text-warn" : "text-muted"}`}>{children}</p>;
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  error,
  inputMode,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string | null;
  inputMode?: "numeric" | "decimal" | "text";
  /** Holds still while the form's own request is on its way, keeping the cursor. */
  readOnly?: boolean;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div className="min-w-0 flex-1">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted">
        {label}
      </label>
      <input
        id={id}
        className={`field ${error ? "border-danger focus:border-danger" : ""}`}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && (
        <p id={errorId} className="field-error text-xs">
          {error}
        </p>
      )}
    </div>
  );
}

/** The rest of the reading, folded until asked for. */
export function MoreDetailsToggle({
  open,
  onToggle,
  disabled = false,
}: {
  open: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      disabled={disabled}
      className="flex w-full items-center justify-between rounded-btn border border-line px-3 py-2.5 text-sm font-medium text-white transition-colors hover:border-accent disabled:opacity-60 disabled:hover:border-line"
    >
      <span>More details</span>
      <span className="text-faint">
        <ChevronDownIcon up={open} />
      </span>
    </button>
  );
}

export function Switch({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-3 py-1 text-left text-sm text-white disabled:opacity-50"
    >
      <span>{label}</span>
      <span
        aria-hidden="true"
        className={`relative inline-flex h-6 w-10 shrink-0 rounded-full transition-colors ${
          checked ? "bg-accent" : "bg-surface-strong"
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}
